import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Select,
  Spin,
  Typography,
  Divider,
  List,
  Row,
  Col,
  Alert,
  Image,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DownloadOutlined,
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  CarOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  CalendarOutlined,
  ReloadOutlined,
  EyeFilled,
  FilePdfOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import {
  getSubscriptionRequestById,
  updateSubscriptionRequestStatus,
  downloadSubscriptionRequestAttachment,
  triggerDownload,
} from "../../api/parkpass/subscriptionRequests";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

// Status color mapping
const STATUS_COLORS = {
  submitted: "blue",
  pending: "orange",
  approved: "green",
  rejected: "red",
  under_review: "purple",
  completed: "cyan",
};

// Request type labels
const REQUEST_TYPE_LABELS = {
  first_request: "Première demande",
  renewal: "Renouvellement",
  address_change: "Changement d'adresse",
  vehicle_change: "Changement de véhicule",
  other: "Autre",
};

// Available statuses for update
const AVAILABLE_STATUSES = [
  "submitted",
  "pending",
  "under_review",
  "approved",
  "rejected",
  "completed",
];

// Subscription formula labels
const SUBSCRIPTION_FORMULAS = {
  street_free: "Abonnement Libre",
  street_resident: "Abonnement Résident",
  street_district: "Abonnement Quartier",
  free_formula: "Formule Libre",
  underground_permanent: "Formule Permanente",
  underground_day: "Formule Journée",
  underground_evening: "Formule Soir",
  covered_permanent: "Formule Permanente",
  covered_day: "Formule Journée",
  covered_evening: "Formule Soir",
};

export default function CitizenSubscriptionRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState({});
  const [imagePreviewVisible, setImagePreviewVisible] = useState({});
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);

  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const fetchRequestDetails = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptionRequestById(id, false); // Don't include files
      setRequest(data);
    } catch (error) {
      console.error("Error fetching request details:", error);
      
      if (error.response?.status === 401) {
        message.error("Session expirée. Veuillez vous reconnecter.");
        navigate("/login");
      } else if (error.response?.status === 403) {
        message.error("Vous n'êtes pas autorisé à accéder à cette ressource");
        navigate("/citizen/subscription-requests");
      } else if (error.response?.status === 404) {
        message.error("Demande non trouvée");
        navigate("/citizen/subscription-requests");
      } else {
        message.error(
          error.response?.data?.detail || "Erreur lors du chargement de la demande"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    setDownloading({ ...downloading, [attachment.id]: true });
    try {
      const { blob, filename } = await downloadSubscriptionRequestAttachment(
        id,
        attachment.id
      );
      triggerDownload(blob, filename || attachment.filename);
      message.success(`Téléchargement de ${filename || attachment.filename} réussi`);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      
      if (error.response?.status === 404) {
        message.error("Pièce jointe non trouvée");
      } else {
        message.error(
          error.response?.data?.detail ||
            "Erreur lors du téléchargement de la pièce jointe"
        );
      }
    } finally {
      setDownloading({ ...downloading, [attachment.id]: false });
    }
  };

  const handlePreviewAttachment = async (attachment) => {
    const isImage = attachment.content_type?.startsWith('image/');
    const isPdf = attachment.content_type === 'application/pdf';

    if (!isImage && !isPdf) {
      message.warning('L\'aperçu n\'est disponible que pour les images et les PDF');
      return;
    }

    try {
      const { blob } = await downloadSubscriptionRequestAttachment(
        id,
        attachment.id
      );
      const url = window.URL.createObjectURL(blob);
      
      if (isPdf) {
        setPdfPreviewUrl(url);
        setPdfPreviewVisible(true);
      } else if (isImage) {
        setAttachmentPreviews(prev => ({ ...prev, [attachment.id]: url }));
        setImagePreviewVisible(prev => ({ ...prev, [attachment.id]: true }));
      }
    } catch (error) {
      console.error('Preview failed', error);
      message.error('Erreur lors de l\'aperçu de la pièce jointe');
    }
  };

  const showStatusUpdateModal = () => {
    setNewStatus(request?.status || "");
    setIsStatusModalVisible(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      message.warning("Veuillez sélectionner un statut");
      return;
    }

    if (newStatus === request?.status) {
      message.info("Le statut est déjà à cette valeur");
      setIsStatusModalVisible(false);
      return;
    }

    setUpdating(true);
    try {
      await updateSubscriptionRequestStatus(id, newStatus);
      message.success("Statut mis à jour avec succès");
      setIsStatusModalVisible(false);
      
      // Refresh the details
      await fetchRequestDetails();
    } catch (error) {
      console.error("Error updating status:", error);
      
      if (error.response?.status === 400) {
        message.error(
          error.response?.data?.detail || "Statut invalide"
        );
      } else {
        message.error(
          error.response?.data?.detail || "Erreur lors de la mise à jour du statut"
        );
      }
    } finally {
      setUpdating(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  if (loading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Spin size="large" tip="Chargement des détails..." />
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ padding: "24px" }}>
        <Alert
          message="Demande non trouvée"
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate("/citizen/subscription-requests")}>
              Retour à la liste
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/citizen/subscription-requests")}
            >
              Retour
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined /> Demande #{request.id}
            </Title>
          </Space>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchRequestDetails}
              loading={loading}
            >
              Actualiser
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={showStatusUpdateModal}
            >
              Modifier le statut
            </Button>
          </Space>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        {/* Status and Timeline */}
        <Col xs={24} lg={24}>
          <Card title="Statut et dates" bordered={false}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Statut actuel">
                <Tag
                  color={STATUS_COLORS[request.status] || "default"}
                  style={{ fontSize: 14, padding: "4px 12px" }}
                >
                  {request.status?.toUpperCase() || "N/A"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date de création">
                <Space>
                  <CalendarOutlined style={{ color: "#1890ff" }} />
                  <Text>{dayjs(request.created_at).format("DD/MM/YYYY HH:mm")}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Dernière mise à jour">
                <Space>
                  <CalendarOutlined style={{ color: "#52c41a" }} />
                  <Text>{dayjs(request.updated_at).format("DD/MM/YYYY HH:mm")}</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Applicant Information */}
        <Col xs={24} lg={12}>
          <Card title="Informations du demandeur" bordered={false}>
            <Descriptions column={1}>
              <Descriptions.Item
                label={
                  <Space>
                    <UserOutlined />
                    <Text>Nom complet</Text>
                  </Space>
                }
              >
                <Text strong>
                  {request.full_name ||
                    (request.first_name || request.last_name
                      ? `${request.first_name || ""} ${request.last_name || ""}`.trim()
                      : "N/A")}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <IdcardOutlined />
                    <Text>CIN/Passeport</Text>
                  </Space>
                }
              >
                <Text>{request.cin_passport || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <PhoneOutlined />
                    <Text>Téléphone</Text>
                  </Space>
                }
              >
                <Text copyable>{request.phone || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <HomeOutlined />
                    <Text>Adresse</Text>
                  </Space>
                }
              >
                <Text>{request.address || "N/A"}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Request Information */}
        <Col xs={24} lg={12}>
          <Card title="Détails de la demande" bordered={false}>
            <Descriptions column={1}>
              <Descriptions.Item label="Type de demande">
                <Tag color="blue">
                  {REQUEST_TYPE_LABELS[request.request_type] ||
                    request.request_type ||
                    "N/A"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Formule d'abonnement">
                <Tag color="blue">
                  {SUBSCRIPTION_FORMULAS[request.subscription_formula] ||
                    request.subscription_formula ||
                    "N/A"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <EnvironmentOutlined />
                    <Text>Parking</Text>
                  </Space>
                }
              >
                <Text strong>{request.parking_name || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space>
                    <CarOutlined />
                    <Text>Matricule</Text>
                  </Space>
                }
              >
                <Text>{request.matricule || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Marque">
                <Text>{request.marque || "N/A"}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Attachments */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <Text>Pièces jointes ({request.attachments?.length || 0})</Text>
              </Space>
            }
            bordered={false}
          >
            {request.attachments && request.attachments.length > 0 ? (
              <List
                dataSource={request.attachments}
                renderItem={(attachment) => {
                  const isImage = attachment.content_type?.startsWith('image/');
                  const isPdf = attachment.content_type === 'application/pdf';
                  const previewUrl = attachmentPreviews[attachment.id];

                  return (
                    <List.Item
                      actions={[
                        (isImage || isPdf) && (
                          <Button
                            type="primary"
                            icon={<EyeFilled />}
                            onClick={() => handlePreviewAttachment(attachment)}
                          >
                            Aperçu
                          </Button>
                        ),
                        <Button
                          type="default"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadAttachment(attachment)}
                          loading={downloading[attachment.id]}
                        >
                          Télécharger
                        </Button>,
                      ].filter(Boolean)}
                    >
                      {isImage && previewUrl && (
                        <Image
                          width={0}
                          height={0}
                          style={{ display: 'none' }}
                          src={previewUrl}
                          preview={{
                            visible: imagePreviewVisible[attachment.id] || false,
                            onVisibleChange: (visible) => {
                              setImagePreviewVisible(prev => ({ ...prev, [attachment.id]: visible }));
                              if (!visible && previewUrl) {
                                window.URL.revokeObjectURL(previewUrl);
                                setAttachmentPreviews(prev => {
                                  const updated = { ...prev };
                                  delete updated[attachment.id];
                                  return updated;
                                });
                              }
                            }
                          }}
                        />
                      )}
                      <List.Item.Meta
                        avatar={
                          isImage ? (
                            <FileImageOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                          ) : isPdf ? (
                            <FilePdfOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
                          ) : (
                            <FileTextOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                          )
                        }
                        title={<Text strong>{attachment.filename}</Text>}
                        description={
                          <Space split="|">
                            <Text type="secondary">{attachment.content_type}</Text>
                            <Text type="secondary">
                              {formatFileSize(attachment.size_bytes)}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Alert
                message="Aucune pièce jointe"
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Status Update Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>Modifier le statut</span>
          </Space>
        }
        open={isStatusModalVisible}
        onOk={handleStatusUpdate}
        onCancel={() => setIsStatusModalVisible(false)}
        confirmLoading={updating}
        okText="Mettre à jour"
        cancelText="Annuler"
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Statut actuel:</Text>
          <Tag color={STATUS_COLORS[request.status] || "default"} style={{ fontSize: 14 }}>
            {request.status?.toUpperCase()}
          </Tag>
          <Divider />
          <Text>Nouveau statut:</Text>
          <Select
            value={newStatus}
            onChange={setNewStatus}
            style={{ width: "100%" }}
            size="large"
          >
            {AVAILABLE_STATUSES.map((status) => (
              <Option key={status} value={status}>
                <Tag color={STATUS_COLORS[status] || "default"}>
                  {status.toUpperCase()}
                </Tag>
              </Option>
            ))}
          </Select>
        </Space>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        title="Aperçu PDF"
        open={pdfPreviewVisible}
        onCancel={() => {
          setPdfPreviewVisible(false);
          if (pdfPreviewUrl) {
            window.URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl(null);
          }
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        {pdfPreviewUrl && (
          <iframe
            src={pdfPreviewUrl}
            style={{ width: '100%', height: '80vh', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </div>
  );
}
